import { CSSProperties, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Image, Spin, Tag, theme, Typography } from 'antd';
import { CalendarOutlined, CheckOutlined, ClockCircleOutlined, EnvironmentOutlined, HourglassOutlined } from '@ant-design/icons';
import Title from 'antd/lib/typography/Title';
import BookyBox from '../../Components/Layout/BookyBox';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';
import NotFound from '../../Components/Page/NotFoundPage';
import { TextService } from '../../Services/TextService';
import { EventType } from '../../Types/EventType';
import { isOnlyComing } from '../../Types/AnswerOptions';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { formatDateString, getTimeRangeString } from '../../Helpers/DateHelpers';
import { memberEvent, memberEventTime, PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiCompanyDtoCompanyPublicSiteDto } from '../../Repository/eventbookyapi';
import { CustomerAnswer } from '../../Types/RequestResponse/createReplyRequest';
import './PublicSiteEventsPage.css';

// A tag without a colour gets a black background in this app: the theme gives the container colour as the
// name "white", which the tag can't mix with. The neutral tags here set their colours themselves.
const neutralTagStyle = { background: "#fafafa", color: "rgba(0, 0, 0, 0.88)", borderColor: "#d9d9d9" };

enum State {
    Loading,
    CompanyNotFound,
    Ready,
    LoadError
}

/**
 * Own route/tab (`/events`) where a logged-in member sees the coming events the company has chosen
 * to show on the member site, with the member's own answer for each time. Which events are listed is
 * decided by the API. Answering happens on the ordinary event page, which the member reaches already
 * logged in.
 */
const PublicSiteEventsPage = () => {
    const params = useParams();
    const navigation = useNavigate();
    const GetText = TextService.GetText;
    const { token } = theme.useToken();
    const isSwedish = getCurrentLanguage() === 'se';
    const locale = isSwedish ? 'sv-SE' : 'en-GB';

    const [state, setState] = useState(State.Loading);
    const [publicSite, setPublicSite] = useState({} as WebApiCompanyDtoCompanyPublicSiteDto);
    const [events, setEvents] = useState([] as memberEvent[]);

    useEffect(() => {
        const loadData = async () => {
            if (!params.companyurl) {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                setPublicSite(company);
            } catch {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                setEvents(await PublicSiteService.GetMemberEvents());
                setState(State.Ready);
            } catch {
                setState(State.LoadError);
            }
        };

        loadData();
    }, [params.companyurl]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    // The year is only shown when it isn't this year.
    const isOtherYear = (date: Date) => date.getFullYear() !== new Date().getFullYear();

    // Weekday and month are written out ("lör 24 okt"), which is quicker to read than 2026-10-24.
    const formatDay = (date: string) => {
        const day = new Date(date);
        const year = isOtherYear(day) ? 'numeric' : undefined;
        return day.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year }).replace(/\./g, '');
    };

    const formatTime = (time: memberEventTime) => {
        return time.isFullDay ? formatDay(time.start) : `${formatDay(time.start)} · ${getTimeRangeString(time.start, time.end, isSwedish)}`;
    };

    // The member's own answer comes first. Without one, the tag says whether there is a place to get.
    // A no is named like the button the member clicked: "Kommer inte" on a sign-up, "Kan inte delta" in a poll.
    // A sign-up with only "Kommer" has no such button, so a no there is a cancelled sign-up.
    const renderStatus = (event: memberEvent, time: memberEventTime) => {
        switch (time.answer) {
            case CustomerAnswer.Accept:
                return <Tag color="green" icon={<CheckOutlined />}>{GetText('PublicSiteEvents_Accepted')}</Tag>;
            case CustomerAnswer.Waitlist:
                return <Tag color="orange">{GetText('PublicSiteEvents_Waitlisted')}</Tag>;
            case CustomerAnswer.Tentative:
                return <Tag color="blue">{GetText('PublicSiteEvents_Tentative')}</Tag>;
            case CustomerAnswer.Reject:
                return <Tag style={neutralTagStyle}>{GetText(event.eventType === EventType.Poll ? 'PublicSiteEvents_Rejected' : isOnlyComing(event) ? 'Event_Cancelled' : 'Event_NotComing')}</Tag>;
        }

        if (time.isFullyBooked) {
            return <Tag color="red">{GetText(time.isWaitlistOpen ? 'PublicSiteEvents_FullyBookedWaitlist' : 'Event_TimeFullyBooked')}</Tag>;
        }

        return <Tag color="gold">{GetText('PublicSiteEvents_NotAnswered')}</Tag>;
    };

    const openEvent = (event: memberEvent) => {
        // Tells the event page that the visitor comes from the member site and already is logged in.
        navigation(`/${getCurrentLanguage()}/${params.companyurl}/Event/${event.uniqueId}`, { state: { asMember: true } });
    };

    // "Sign up" would promise too much when every time is full and has no waitlist.
    const getButtonText = (event: memberEvent) => {
        if (event.hasReplied) {
            return GetText('PublicSiteEvents_ChangeAnswer');
        }

        if (event.eventType === EventType.Poll) {
            return GetText('PublicSiteEvents_Vote');
        }

        const canGetPlace = event.times.some(time => !time.isFullyBooked || time.isWaitlistOpen);
        return GetText(canGetPlace ? 'PublicSiteEvents_Answer' : 'PublicSiteEvents_Show');
    };

    const renderDateBlock = (event: memberEvent) => {
        const start = new Date(event.times[0].start);

        return (
            <div className="member-event-date">
                <small>{start.toLocaleDateString(locale, { weekday: 'short' }).replace(/\./g, '')}</small>
                <b>{start.getDate()}</b>
                <small>{start.toLocaleDateString(locale, { month: 'short' }).replace(/\./g, '')}</small>
                {isOtherYear(start) ? <small>{start.getFullYear()}</small> : ''}
            </div>
        );
    };

    // An event with one time shows it in the info row and its answer at the top. With several times each
    // time gets its own row with its own answer.
    const renderEvent = (event: memberEvent) => {
        const singleTime = event.times.length === 1 ? event.times[0] : undefined;

        return (
            <div key={event.uniqueId} className="member-event">
                {event.times.length > 0 ? renderDateBlock(event) : ''}

                <div className="member-event-body">
                    <div className="member-event-top">
                        <h3>{event.name}</h3>
                        {singleTime ? renderStatus(event, singleTime) : ''}
                    </div>

                    <div className="member-event-meta">
                        {singleTime ? <span><ClockCircleOutlined />{formatTime(singleTime)}</span> : ''}
                        {event.location ? <span><EnvironmentOutlined />{event.location}</span> : ''}
                        {event.deadline ? <span><HourglassOutlined />{GetText('PublicSiteEvents_LastDay')} {formatDateString(event.deadline)}</span> : ''}
                    </div>

                    {event.description ? (
                        <Typography.Paragraph className="member-event-description" ellipsis={{ rows: 3, expandable: true }}>
                            {event.description}
                        </Typography.Paragraph>
                    ) : ''}

                    {!singleTime && event.times.map((time, index) => (
                        <div key={index} className="member-event-time">
                            <span><CalendarOutlined /> {formatTime(time)}</span>
                            {renderStatus(event, time)}
                        </div>
                    ))}

                    <Button type={event.hasReplied ? 'default' : 'primary'} onClick={() => openEvent(event)}>
                        {getButtonText(event)}
                    </Button>
                </div>
            </div>
        );
    };

    if (state === State.CompanyNotFound) {
        return <NotFound message={GetText('PublicSiteEditMember_CompanyNotFound')} />;
    }

    if (state === State.Loading) {
        return (
            <PagePublicSite>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </PagePublicSite>
        );
    }

    // The accent follows the theme's primary colour, the same as the buttons.
    const accentStyle = { '--member-event-accent': token.colorPrimary } as CSSProperties;

    return (
        <PagePublicSite>
            {publicSite.siteLogoPath ? (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                </div>
            ) : (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                </div>
            )}
            <PublicSiteMenu />

            <BookyBox>
                <div className="member-events" style={accentStyle}>
                    <h2>{GetText('PublicSiteEvents_Heading')}</h2>
                    <p className="member-events-intro">{GetText('PublicSiteEvents_Intro')}</p>

                    {state === State.LoadError ? (
                        <Alert type="error" showIcon message={GetText('PublicSiteEvents_LoadError')} />
                    ) : events.length === 0 ? (
                        <i>{GetText('PublicSiteEvents_Empty')}</i>
                    ) : (
                        events.map(renderEvent)
                    )}
                </div>
            </BookyBox>
        </PagePublicSite>
    );
};

export default PublicSiteEventsPage;
